import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Search, X, ArrowUpRight, Tag } from "lucide-react";
import { useNavigate } from "react-router";
import { useBurrow } from "./BurrowStore";
import type { Thought } from "./ThoughtCard";

interface SearchOverlayProps {
  onSelectThought?: (thought: Thought) => void;
}

function getMetaKeyLabel(): string | null {
  if (typeof window === "undefined") return null;
  const ua = window.navigator.userAgent || "";
  const isMobile = /Mobi|Android|iPhone|iPad/i.test(ua);
  if (isMobile) return null;

  const isApple = /Mac|iPhone|iPad|iPod/i.test(ua);
  return isApple ? "⌘" : "Ctrl";
}

export function SearchOverlay({ onSelectThought }: SearchOverlayProps) {
  const { searchThoughts, searchAllThoughts } = useBurrow();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [selectedThought, setSelectedThought] = useState<Thought | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const metaKey = getMetaKeyLabel();

  // PRD §4.1 — Your thoughts first. Bar surfaces up to 3-4 cards from user's own
  // library. There is no toggle that overrides this default.
  //
  // Searched server-side. The in-memory filter only saw the pages already
  // loaded, which broke this bar twice over: it hid the user's own matches, and
  // because `hasOwnMatches` picks which pane the Search button opens, a miss
  // actively routed them to *community* results for a card they own.
  const trimmed = query.trim();
  const localResults = trimmed.length >= 2 ? searchThoughts(trimmed) : [];
  const [serverResults, setServerResults] = useState<{ query: string; results: Thought[] } | null>(null);

  useEffect(() => {
    if (trimmed.length < 2) {
      setServerResults(null);
      return;
    }
    let cancelled = false;
    const id = setTimeout(async () => {
      const res = await searchAllThoughts(trimmed);
      if (!cancelled) setServerResults({ query: trimmed, results: res.results });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [trimmed, searchAllThoughts]);

  // Local matches stand in until the query settles, so the bar responds
  // immediately instead of blanking on every keystroke.
  const rawResults = serverResults?.query === trimmed ? serverResults.results : localResults;
  const results = rawResults.slice(0, 4);
  const hasOwnMatches = rawResults.length > 0;
  const showResults = focused && (query.trim().length >= 2 || query.trim().length === 0);

  // PRD §4.1 — When no own matches, Search button routes to community side of the
  // two-pane search page. With matches, it routes to the user side (default).
  const openSearchHub = () => {
    const q = query.trim();
    if (q.length < 2) {
      navigate("/search");
    } else if (hasOwnMatches) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
    } else {
      navigate(`/search?q=${encodeURIComponent(q)}&focus=community`);
    }
    setFocused(false);
    setQuery("");
    setSelectedThought(null);
  };

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setFocused(false);
        inputRef.current?.blur();
      }
      // Cmd+K to focus
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setFocused(true);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const handleSelect = (thought: Thought) => {
    setSelectedThought(thought);
    setFocused(false);
    setQuery("");
    onSelectThought?.(thought);
  };

  return (
    <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl flex-shrink-0">
      <div
        className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border transition-all duration-200 ${
          focused
            ? "border-accent/40 bg-card shadow-sm"
            : "border-border/40 bg-card/60"
        }`}
      >
        <button onClick={openSearchHub} className="shrink-0 text-muted-foreground hover:text-accent transition-colors">
          <Search size={15} />
        </button>
        <input
          ref={inputRef}
          type="text"
          placeholder="Search thoughts..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              openSearchHub();
            }
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 200)}
          className="bg-transparent outline-none w-full min-w-0 text-foreground placeholder:text-muted-foreground/50"
          style={{ fontSize: "0.82rem", fontFamily: "'Fraunces', Georgia, serif" }}
        />
        {/* The shortcut used to live inside the placeholder, so the input's own
            truncation ate it — "(Ctrl+" — whenever the nav squeezed the bar.
            As a shrink-0 sibling it's the label that gives way instead, and the
            hint stays whole on both ⌘ and the wider Ctrl. */}
        {metaKey && !query && (
          <kbd
            className="hidden lg:inline-flex shrink-0 items-center gap-0.5 px-1.5 py-0.5 rounded-md border border-border/40 bg-muted/40 text-muted-foreground/60 font-sans"
            style={{ fontSize: "0.65rem", lineHeight: 1.4 }}
          >
            {metaKey}
            <span aria-hidden>+</span>K
          </kbd>
        )}
        {query && (
          <button
            onClick={() => { setQuery(""); inputRef.current?.focus(); }}
            className="text-muted-foreground/40 hover:text-foreground shrink-0"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      <AnimatePresence>
        {showResults && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full mt-2 right-0 w-[360px] max-h-[400px] bg-card border border-border/50 rounded-2xl shadow-2xl shadow-foreground/10 overflow-hidden z-[80]"
          >
            {results.length > 0 ? (
              <div className="overflow-y-auto max-h-[340px] p-2">
                <p className="text-muted-foreground/50 px-3 pt-1 pb-2" style={{ fontSize: "0.72rem" }}>
                  From your library
                </p>
                {results.map((thought) => (
                  <button
                    key={thought.id}
                    onMouseDown={(e) => { e.preventDefault(); handleSelect(thought); }}
                    className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-muted/30 transition-all group"
                  >
                    {thought.title && (
                      <p
                        className="text-primary mb-0.5 line-clamp-1"
                        style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "0.88rem" }}
                      >
                        {thought.title}
                      </p>
                    )}
                    <p className="text-foreground/70 line-clamp-2" style={{ fontSize: "0.8rem", lineHeight: "1.45" }}>
                      {thought.body}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {(thought.labels ?? []).slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="text-muted-foreground/50 flex items-center gap-0.5"
                          style={{ fontSize: "0.65rem" }}
                        >
                          <Tag size={8} />
                          {tag}
                        </span>
                      ))}
                      {thought.source_title && (
                        <span className="text-muted-foreground/40 flex items-center gap-0.5" style={{ fontSize: "0.65rem" }}>
                          <ArrowUpRight size={8} />
                          {thought.source_title}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : query.trim().length >= 2 ? (
              <div className="text-center py-7 px-6">
                <motion.div
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                  className="text-accent/60 mb-2"
                  style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "0.95rem", fontStyle: "italic" }}
                >
                  Nothing of yours yet.
                </motion.div>
                <p className="text-muted-foreground/70" style={{ fontSize: "0.78rem", lineHeight: 1.55 }}>
                  Try the wider room — see what the community has been thinking about “{query}.”
                </p>
              </div>
            ) : null}

            {/* Centered Search button — routes to community side when no own matches */}
            <div className="border-t border-border/40 p-2 bg-card/80">
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={openSearchHub}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-accent/10 hover:bg-accent/15 border border-accent/20 transition-all"
                style={{ fontSize: "0.85rem" }}
              >
                <Search size={16} className="text-accent/80" />
                {query.trim().length >= 2 && !hasOwnMatches ? "Search the community" : "Search"}
                <ArrowUpRight size={14} className="text-accent/70" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
